<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { sessionHelper, ddlHelper } from "@/utils";
import { SESSION, SHARED } from "@/constants";
import { mapState } from "vuex";
import { USERS_MODULE } from "@/state/modules/users";
import { usersMethods } from "@/state/helpers";

export default {
  page: { title: i18n.t("label.listingX", [i18n.t("module.user")]) },
  components: { Layout, PageHeader },
  data() {
    return {
      title: i18n.t("label.listingX", [i18n.t("module.user")]),
      breadcrumbItems: [
        { text: i18n.t("module.admin"), href: "/" },
        { text: i18n.t("module.user"), active: true },
      ],
      statusDdl: ddlHelper.getActivationStatus(true),
      currentPage: 1,
      filter: "",
      perPage: SHARED.DEFAULT_PAGE_SIZE,
      fields: [
        { key: "name", sortable: true, label: i18n.t("label.name") },
        { key: "email", sortable: true, label: i18n.t("label.email") },
        { key: "agentCode", sortable: true, label: i18n.t("label.agentCode") },
        { key: "roles", sortable: false, label: i18n.t("label.role") },
        { key: "isActive", sortable: true, label: i18n.t("label.status") },
        { key: "isAllowLogin", sortable: true, label: i18n.t("user.allowLogin") },
        { key: "isLocked", sortable: true, label: i18n.t("label.accountStatus") },
        { key: "action", sortable: false, label: i18n.t("label.action") },
      ],
      searchCriteria: {},
    };
  },
  computed: {
    ...mapState(USERS_MODULE, ["status"]),
    tableRows() {
      return this.$store.state.users.list.data;
    },
    pagination() {
      return this.$store.state.users.list.pagination;
    },
  },
  created() {
    this.initSearchCriteria();
  },
  methods: {
    ...usersMethods,
    initSearchCriteria() {
      let isDefault = true;
      if (sessionHelper.hasSearchCriteria(SESSION.USERS)) {
        isDefault = false;
        this.searchCriteria = sessionHelper.retrieveSearch(SESSION.USERS);
      }
      if (isDefault) {
        this.searchCriteria = {
          pageNumber: SHARED.DEFAULT_PAGE_NUMBER,
          pageSize: SHARED.DEFAULT_PAGE_SIZE,
          isActive: this.statusDdl[0].value,
          searchValue: "",
          searchArray: [],
        };
      }
      this.getList(this.searchCriteria);
    },
    getList(filter) {
      this.getAllUser({ filter });
      this.searchCriteria = filter;
      sessionHelper.storeSearch(SESSION.USERS, filter);
    },
    pageNavigation(pageNumber) {
      const filter = sessionHelper.retrieveSearch(SESSION.USERS);
      filter.pageNumber = pageNumber;
      this.getList(filter);
    },
    searchListing() {
      this.searchCriteria.pageNumber = SHARED.DEFAULT_PAGE_NUMBER;
      this.searchCriteria.searchArray = [this.filter];
      this.getList(this.searchCriteria);
    },
    toggleStatus(row) {
      this.updateUsersStatus({
        id: row.id,
        isActive: !row.isActive,
        filter: this.searchCriteria,
      });
    },
    toggleLoginAccess(row) {
      this.updateUsersLoginAccess({
        id: row.id,
        isAllowLogin: !row.isAllowLogin,
        filter: this.searchCriteria,
      });
    },
    unlock(row) {
      this.unlockUsers({ id: row.id, filter: this.searchCriteria });
    },
    goCreate() {
      this.$router.push({ name: "User-create" });
    },
    goEdit(row) {
      this.$router.push({ name: "User-update", params: { id: row.id } });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbItems" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mt-2 mb-3">
              <div class="col-sm-12 col-md-6">
                <label class="d-inline-flex align-items-center">
                  {{ $t('label.search') }}
                  <b-form-input v-model="filter" type="search" :placeholder="$t('label.search')"
                    class="form-control form-control-sm ml-2" @change="searchListing"></b-form-input>
                </label>
              </div>
              <div class="col-sm-12 col-md-6 text-md-right">
                <b-button v-if="$can('Create User')" variant="primary" size="sm" @click="goCreate">
                  <i class="mdi mdi-plus mr-1"></i>{{ $t('label.createX', [$t('module.user')]) }}
                </b-button>
              </div>
            </div>
            <div class="table-responsive mb-0">
              <b-table :items="tableRows" :fields="fields" responsive="sm" :per-page="perPage">
                <template #cell(roles)="data">
                  <b-badge variant="info" class="mr-1" v-for="r in tableRows[data.index].roles" :key="r.id">{{ r.name }}</b-badge>
                </template>
                <template #cell(isActive)="data">
                  <b-badge variant="success" v-if="tableRows[data.index].isActive">{{ $t('label.active') }}</b-badge>
                  <b-badge variant="secondary" v-else>{{ $t('label.inactive') }}</b-badge>
                </template>
                <template #cell(isAllowLogin)="data">
                  <b-form-checkbox
                    switch
                    :disabled="!$can('Update User')"
                    :checked="tableRows[data.index].isAllowLogin"
                    @change="toggleLoginAccess(tableRows[data.index])"
                  >
                    <span :class="tableRows[data.index].isAllowLogin ? 'text-success' : 'text-muted'">
                      {{ tableRows[data.index].isAllowLogin ? $t('label.yes') : $t('label.no') }}
                    </span>
                  </b-form-checkbox>
                </template>
                <template #cell(isLocked)="data">
                  <b-badge variant="danger" v-if="tableRows[data.index].isLocked">{{ $t('label.lock') }}</b-badge>
                  <b-badge variant="success" v-else>{{ $t('label.unlock') }}</b-badge>
                </template>
                <template #cell(action)="data">
                  <a v-if="$can('Update User')" href="javascript:void(0);" @click="goEdit(tableRows[data.index])" class="text-info mr-2" v-b-tooltip.hover :title="$t('label.updateX', [$t('module.user')])">
                    <i class="mdi mdi-pencil font-size-18"></i>
                  </a>
                  <a v-if="$can('Update User')" href="javascript:void(0);" @click="toggleStatus(tableRows[data.index])" class="text-warning mr-2" v-b-tooltip.hover :title="$t('label.status')">
                    <i class="mdi mdi-power font-size-18"></i>
                  </a>
                  <a href="javascript:void(0);" v-if="tableRows[data.index].isLocked && $can('Update User')" @click="unlock(tableRows[data.index])" class="text-danger" v-b-tooltip.hover :title="$t('label.unlock')">
                    <i class="mdi mdi-lock-open-outline font-size-18"></i>
                  </a>
                </template>
              </b-table>
            </div>
            <div class="row">
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination v-model="currentPage" :total-rows="pagination.total" :per-page="perPage" @change="pageNavigation"></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
